import React, { useReducer } from 'react';
import {
  Box,
  TextInput,
  Button,
  ArrowLeftIcon,
  ActionList,
  DropdownOverlay,
  Dropdown,
  ActionListItem,
  SelectInput,
  useToast,
} from '@razorpay/blade/components';
import { useForm } from 'forms';
import { useHistory } from 'react-router-dom';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from '../components/styles';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';
import { routePaths } from 'components/Routes/data';
import { CompensationFormFields } from './types';
import {
  compensationValidationSchema,
  getAmountPayload,
  getCompensationStepPayload,
  getCompensationStructureFromCustomComponents,
  invalidateDraftEmployeeCache,
  updateInitialCompensationFormData,
} from './utils';
import {
  CUSTOM_SALARY_STRUCTURE_ID,
  customSalaryStructureOption,
  initialCompensationData,
} from './data';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { CustomSalaryStructureForm } from './components/salaryStructure/CustomSalaryStructureForm';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError } from 'utils/AppError';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { useEmployeeCustomSalaryStructure } from './components/salaryStructure/hooks/useEmployeeCustomSalaryStructure';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { useListSalaryStructures } from 'components/SalaryStructures/hooks';
import { useSalaryStructureComponents } from 'components/SalaryStructures/creation/hooks/useSalaryStructureComponents';
import { SalaryStructure } from 'components/SalaryStructure/types';
import SalaryStructureDropDown from 'components/SalaryStructures/creation/components/SalaryStructureDropDown';
import { toIndianCurrencyString, uniqueId } from 'utils/Numbers';
import { INTEGER_AMOUNT } from 'utils/regexes';
import { customSalaryComponentsReducer } from 'components/WizardViews/RevisePay/components/CustomSalaryComponents/reducer';
import {
  getInitialCustomComponents,
  validateCustomSalaryStructure,
} from 'components/WizardViews/RevisePay/components/CustomSalaryComponents/utils';
import { CustomSalaryComponents } from 'components/WizardViews/RevisePay/components/CustomSalaryComponents';
import { CustomSalaryComponent } from 'components/WizardViews/RevisePay/components/CustomSalaryComponents/types';

export const CompensationDetails = ({
  setShowDiscardModal,
  setShowSaveDraftAndCloseModal,
}: {
  setShowDiscardModal: (value: boolean) => void;
  setShowSaveDraftAndCloseModal: (value: boolean) => void;
}) => {
  const { draftEmployeeId } = useDraftEmployeeId();
  const {
    register,
    formData,
    validate,
    setIsSubmitting,
    isSubmitting,
    setFormData,
    setFieldValue,
  } = useForm<CompensationFormFields>(initialCompensationData);
  const history = useHistory();
  const {
    isMileStone1Enabled: isRearchEnabled,
    isM3S1Enabled,
    isM3S3Enabled,
  } = useIsFeatureDisabledForRearch();
  const isRearchDisabled = !isRearchEnabled;
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleStepBack = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.basicDetails,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const { isLoading, isFetching, data } = useDraftEmployeeData({
    onSuccess: (data) => updateInitialCompensationFormData(data, setFormData, isM3S3Enabled),
  });
  const {
    data: salaryStructureList,
    isFetching: isFetchingSalaryStructureList,
    error: listSalaryStructureError,
  } = useListSalaryStructures({
    queryKey: ['salaryStructuresForEmployeeOnboarding'],
    filters: 'SALARY_STRUCTURE_STATUS_ACTIVE',
    enabled: isM3S1Enabled && !isFetching,
    onSuccess: (salaryStructures: SalaryStructure[]) => {
      const defaultSalaryStructureId: string =
        salaryStructures?.find((item) => item?.type === 'SALARY_STRUCTURE_TYPE_DEFAULT')?.id || '';

      let salaryStructureId: string;
      if (isM3S3Enabled && formData.salaryStructureId?.[0] === CUSTOM_SALARY_STRUCTURE_ID) {
        salaryStructureId = CUSTOM_SALARY_STRUCTURE_ID;
      } else if (!data?.compensationDetails?.salaryStructureId) {
        salaryStructureId = defaultSalaryStructureId as string;
      } else {
        const currentSalaryStructure = data?.compensationDetails?.salaryStructureId;
        const isSalaryStructureDisabled = !salaryStructures?.find(
          (item) => item?.id === currentSalaryStructure,
        )?.id;

        salaryStructureId = isSalaryStructureDisabled ? '' : currentSalaryStructure;
      }

      setFormData({ ...formData, salaryStructureId: [salaryStructureId] });
    },
  });

  const templates = salaryStructureList ?? [];

  const transformedRearchComponents =
    data?.compensationDetails?.earnings?.rearchComponents?.map((component) => ({
      id: uniqueId(),
      componentId: component.id,
      isPredefined: true,
      name: component.name,
      amount: component.value != null ? component.value.toString() : '',
      category: component.category,
      componentType: component.componentType,
    })) ?? ([] as CustomSalaryComponent[]);

  const { allNonAdhocComponents, isLoading: isFetchingComponents, refetch: refetchComponents } = useSalaryStructureComponents({
    enabled: isM3S3Enabled,
    onSuccess: (fetchedData) => {
      dispatchCustomComponents({
        type: 'INITIALIZE_COMPONENTS',
        payload: {
          initialComponents: getInitialCustomComponents({
            allNonAdhocComponents: fetchedData.allNonAdhocComponents,
            exisitingCustomSalaryStructure: transformedRearchComponents,
            formData: { isCustomSalaryStructure: true },
          }),
        },
      });
    },
  });

  const [customComponents, dispatchCustomComponents] = useReducer(
    customSalaryComponentsReducer,
    getInitialCustomComponents({
      allNonAdhocComponents: allNonAdhocComponents ?? [],
      exisitingCustomSalaryStructure: transformedRearchComponents,
      formData: {
        isCustomSalaryStructure: isM3S3Enabled && formData.salaryStructureType?.[0] === 'custom',
      },
    }),
  );

  const employeeCustomSalaryStructureHandlers = useEmployeeCustomSalaryStructure({
    compensationDetails: data?.compensationDetails,
  });

  const { mutateAsync: updateDraftEmployeeCompensationStep } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.updateDraftEmployeeCompensationStep,
    onError: (error: typeof AppError) => {
      setIsSubmitting(false);
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent, onSuccess: () => void) => {
    e.preventDefault();

    const isValidForm = validate(compensationValidationSchema);
    const isCustomStructure =
      formData.salaryStructureType?.[0] === 'custom' ||
      formData.salaryStructureId?.[0] === CUSTOM_SALARY_STRUCTURE_ID;
    const validateSalaryStructureId = isM3S1Enabled && !formData.salaryStructureId?.[0]?.trim();
    if (!isValidForm || validateSalaryStructureId) {
      return;
    }
    if (isM3S3Enabled && isCustomStructure) {
      const customValidationError = validateCustomSalaryStructure(
        customComponents,
        formData.annualCtc ? Number(formData.annualCtc) : undefined,
      );
      if (customValidationError) {
        toast.show({
          content: customValidationError,
          color: 'negative',
        });
        return;
      }
    }
    setIsSubmitting(true);

    let salaryStructureType = formData.salaryStructureType?.[0] ?? 'default';
    let salaryStructureId = formData.salaryStructureId?.[0];
    if (isRearchEnabled && !isM3S1Enabled) {
      salaryStructureType = 'default';
    }
    if (isM3S3Enabled && salaryStructureType === 'custom') {
      salaryStructureId = undefined; //In draft state, custom salary structure id is not present, it gets created when employee is created
    }

    try {
      const res = await updateDraftEmployeeCompensationStep({
        draftEmployeeId,
        input: {
          ...(isM3S1Enabled && salaryStructureId ? { salaryStructureId } : {}),
          salaryStructure: salaryStructureType,
          ctc:
            formData.salaryStructureType?.[0] === 'default' ||
            isM3S1Enabled ||
            (isM3S3Enabled && salaryStructureType === 'custom')
              ? getAmountPayload(formData.annualCtc)
              : undefined,
          variablePay: isRearchDisabled ? getAmountPayload(formData.variablePay) : undefined,
          component:
            salaryStructureType === 'custom'
              ? isM3S3Enabled
                ? getCompensationStructureFromCustomComponents(customComponents)
                : getCompensationStepPayload(employeeCustomSalaryStructureHandlers.components)
              : undefined,
        },
      });

      if (res.payrollUpdateDraftEmployeeCompensation.data.id) {
        onSuccess();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueSuccess = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compliance,
      search: `?draftId=${draftEmployeeId}`,
    });
    invalidateDraftEmployeeCache(queryClient, draftEmployeeId);
  };

  const handleSaveAndCloseSuccess = () => {
    setShowSaveDraftAndCloseModal(true);
    invalidateDraftEmployeeCache(queryClient, draftEmployeeId);
  };

  const onSalaryStructureOptionClick = (data: SalaryStructure) => {
    setFieldValue(
      'salaryStructureType',
      data.id === CUSTOM_SALARY_STRUCTURE_ID ? ['custom'] : ['default'],
    );
  };

  const shouldDisableButtons =
    isLoading || isFetching || isSubmitting || isFetchingSalaryStructureList;

  const {
    onChange: onChangeCompensation,
    value: compensationValue,
    ...restCompensationDetailsProps
  } = register('annualCtc');

  const {
    onChange: onChangeVariablePay,
    value: variablePayValue,
    ...restVariablePayProps
  } = register('variablePay');

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />
      <StyledForm onSubmit={(e) => handleSubmit(e, handleContinueSuccess)}>
        <FormFieldsWrapper shouldShowSpinner={isLoading || isFetching}>
          <FormHeading
            title="Compensation Details"
            description="Calculate the employee's gross pay for payroll processing"
          />
          <BorderedBox>
            {isRearchDisabled && (
              <Dropdown selectionType="single">
                <SelectInput
                  label="Salary Structure"
                  labelPosition="top"
                  {...register('salaryStructureType', 'select')}
                />
                <DropdownOverlay>
                  <ActionList>
                    <ActionListItem title="Default Salary Structure" value="default" />
                    <ActionListItem title="Custom Salary Structure" value="custom" />
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            )}

            {(isM3S1Enabled || isM3S3Enabled) && (
              <SalaryStructureDropDown
                required={true}
                structures={templates}
                registerProps={register('salaryStructureId', 'select')}
                {...(isM3S3Enabled
                  ? {
                      shouldShowCustomOption: true,
                      customOption: customSalaryStructureOption,
                    }
                  : {})}
                onOptionClick={onSalaryStructureOptionClick}
              />
            )}

            {(formData.salaryStructureType?.[0] === 'default' ||
              (isM3S3Enabled && formData.salaryStructureType?.[0] === 'custom')) && (
              <TextInput
                value={toIndianCurrencyString(
                  compensationValue ? Number(compensationValue) : 0,
                  true,
                )}
                onChange={({ value, name }) => {
                  if (INTEGER_AMOUNT.test(value ?? '')) {
                    const numValue = Number(value?.replace(/,/g, '')) ?? 0;
                    onChangeCompensation?.({ name, value: numValue.toString() });
                  }
                }}
                {...restCompensationDetailsProps}
                label="Annual CTC"
                autoFocus
                placeholder="0"
                size="medium"
                necessityIndicator="required"
                isRequired
                type="number"
                prefix="₹"
              />
            )}

            {isRearchDisabled && (
              <TextInput
                value={toIndianCurrencyString(
                  variablePayValue ? Number(variablePayValue) : 0,
                  true,
                )}
                onChange={({ value, name }) => {
                  if (INTEGER_AMOUNT.test(value ?? '')) {
                    const numValue = Number(value?.replace(/,/g, '')) ?? 0;
                    onChangeVariablePay?.({ name, value: numValue.toString() });
                  }
                }}
                {...restVariablePayProps}
                label="Variable Pay"
                placeholder="0"
                size="medium"
                type="number"
                prefix="₹"
                helpText="Annual bonus, incentive etc. that is not included in the monthly salary by default. This is in addition to the Annual CTC"
              />
            )}
          </BorderedBox>

          {formData.salaryStructureType?.[0] === 'custom' ? (
            isM3S3Enabled ? (
              <CustomSalaryComponents
                selectedComponents={customComponents}
                dispatch={dispatchCustomComponents}
                isSubmitting={shouldDisableButtons}
                allComponents={allNonAdhocComponents ?? []}
                isFetchingComponents={isFetchingComponents}
                refetchComponents={refetchComponents}
              />
            ) : (
              <CustomSalaryStructureForm
                isFormBeingSubmitted={shouldDisableButtons}
                employeeCustomSalaryStructureHandlers={employeeCustomSalaryStructureHandlers}
              />
            )
          ) : null}
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={handleStepBack}
            isDisabled={shouldDisableButtons}>
            Back
          </Button>
          <Box display="flex" gap="spacing.3">
            <Button
              variant="secondary"
              size="medium"
              onClick={(e) => handleSubmit(e, handleSaveAndCloseSuccess)}
              isLoading={shouldDisableButtons}>
              Save and Close
            </Button>
            <Button type="submit" variant="primary" size="medium" isLoading={shouldDisableButtons}>
              Continue
            </Button>
          </Box>
        </FormFooterWrapper>
      </StyledForm>
    </>
  );
};